/**
 * Device retirements at one site (migration 20261002160000) and the two
 * staff RPCs that write them, plus the property's Learn manual link so the
 * article can name where it is filed.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { RetirementDisposition, RetirementReason, RetirementRow } from '../lib/retirementArticle';

// device_retirements and its RPCs are newer than database.types.ts;
// regenerate types after `supabase db reset` and drop these casts.
type Result<T> = Promise<{ data: T | null; error: { message: string } | null }>;
type Query<T> = Result<T> & {
  eq: (c: string, v: string) => Query<T>;
  order: (c: string, o?: { ascending?: boolean }) => Query<T>;
  maybeSingle: () => Result<T>;
};
type Untyped = {
  from: (t: string) => { select: (cols: string) => Query<unknown> };
  rpc: (fn: string, args: Record<string, unknown>) => Result<unknown>;
};
const db = supabase as unknown as Untyped;

const COLUMNS =
  'id, location_id, unit_id, retired_on, reason, disposition, replacement, note, snapshot, learn_article_url, learn_filed_at, created_at';

export function useDeviceRetirements(locationId: string | undefined) {
  return useQuery<RetirementRow[]>({
    queryKey: ['device-retirements', locationId],
    enabled: Boolean(locationId),
    queryFn: async () => {
      const { data, error } = await db
        .from('device_retirements')
        .select(COLUMNS)
        .eq('location_id', locationId!)
        .order('retired_on', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as RetirementRow[];
    },
  });
}

export interface PropertyManual {
  code: string | null;
  /** The property manual's Learn link (property_directory.learn_url). */
  learnUrl: string | null;
}

/** The property code and Learn manual link for a location. */
export function usePropertyManual(locationId: string | undefined) {
  return useQuery<PropertyManual>({
    queryKey: ['property-manual', locationId],
    enabled: Boolean(locationId),
    queryFn: async () => {
      const loc = await db.from('locations').select('hierarchy').eq('id', locationId!).maybeSingle();
      if (loc.error) throw new Error(loc.error.message);
      const code = ((loc.data as { hierarchy?: { property_code?: string } } | null)?.hierarchy?.property_code ?? null) || null;
      if (!code) return { code: null, learnUrl: null };
      const dir = await db.from('property_directory').select('learn_url').eq('code', code).maybeSingle();
      if (dir.error) throw new Error(dir.error.message);
      return { code, learnUrl: (dir.data as { learn_url: string | null } | null)?.learn_url ?? null };
    },
    staleTime: 10 * 60 * 1000,
  });
}

export interface RetireInput {
  unitId: string;
  retiredOn: string;
  reason: RetirementReason;
  disposition: RetirementDisposition;
  replacement: string;
  note: string;
}

export function useRetireDevice(locationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RetireInput): Promise<string> => {
      const { data, error } = await db.rpc('retire_rack_unit', {
        p_unit_id: input.unitId,
        p_retired_on: input.retiredOn,
        p_reason: input.reason,
        p_disposition: input.disposition,
        p_replacement: input.replacement,
        p_note: input.note,
      });
      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['device-retirements', locationId] }),
        qc.invalidateQueries({ queryKey: ['admin-rack-plant', locationId] }),
      ]);
    },
  });
}

export function useMarkRetirementFiled(locationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, url }: { id: string; url: string | null }) => {
      const { error } = await db.rpc('mark_retirement_filed', { p_retirement_id: id, p_article_url: url });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['device-retirements', locationId] }),
  });
}
